import { useEffect, useMemo, useRef, useState } from "react";
import { useAppDispatch } from "@/redux/features/hooks";
import { Order, OrderStatus } from "@/redux/services/order/order.type";
import { OrderApi } from "@/redux/services/order/order.api";
import { KpiApi } from "@/redux/services/kpi/kpi.api";
import { useMercureSubscription } from "@/lib/hooks";
import { normalizeOrder } from "@/redux/services/order/normalizeOrder";

// Active statuses use FIFO (oldest first) so the oldest pending/preparing
// order surfaces at the top. Terminal statuses keep recency (newest first)
// since the most recent served/paid/canceled order is usually the relevant one.
const ACTIVE_STATUSES: OrderStatus[] = [
  "pending",
  "accepted",
  "preparing",
  "ready",
];

const sortOrders = (a: Order, b: Order) => {
  if (a.status === "pending" && b.status !== "pending") return -1;
  if (a.status !== "pending" && b.status === "pending") return 1;

  if (a.isModified && !b.isModified) return -1;
  if (!a.isModified && b.isModified) return 1;

  const aTime = new Date(a.createdAt).getTime();
  const bTime = new Date(b.createdAt).getTime();
  const aActive = ACTIVE_STATUSES.includes(a.status);
  return aActive ? aTime - bTime : bTime - aTime;
};

interface Args {
  restaurantId: string;
  page: number;
  initialData: { results: Order[]; count: number } | undefined;
  onInitialDataLoaded?: (count: number) => void;
}

export function useOrderRealtime({
  restaurantId,
  page,
  initialData,
  onInitialDataLoaded,
}: Args) {
  const [orders, setOrders] = useState<Order[]>([]);
  const openDrawersCount = useRef(0);
  const dispatch = useAppDispatch();

  const orderEvent = useMercureSubscription<Order>(
    restaurantId ? `/api/restaurants/${restaurantId}/orders` : null
  );

  useEffect(() => {
    if (initialData?.results) {
      setOrders(initialData.results);
      onInitialDataLoaded?.(initialData.count);
    }
  }, [initialData]);

  useEffect(() => {
    if (!orderEvent) return;
    const type = orderEvent.type;
    const newOrder = normalizeOrder(orderEvent.data);
    if (!newOrder) return;

    setOrders((prevOrders) => {
      const existingIndex = prevOrders.findIndex((o) => o.id === newOrder.id);
      if (existingIndex !== -1) {
        const next = [...prevOrders];
        next[existingIndex] = newOrder;
        return next;
      }
      if (type === "order:created" && page === 1) {
        return [newOrder, ...prevOrders.slice(0, -1)];
      }
      return prevOrders;
    });

    if (openDrawersCount.current === 0) {
      dispatch(OrderApi.util.invalidateTags(["order-list"]));
    }
    dispatch(KpiApi.util.invalidateTags(["kpi"]));
  }, [orderEvent, dispatch, page]);

  const sortedOrders = useMemo(() => {
    if (!orders.length) return [];
    return [...orders].sort(sortOrders);
  }, [orders]);

  const onDrawerOpen = () => {
    openDrawersCount.current += 1;
  };
  const onDrawerClose = () => {
    openDrawersCount.current = Math.max(0, openDrawersCount.current - 1);
  };

  return { orders, sortedOrders, onDrawerOpen, onDrawerClose };
}
